import { Button, Table, Tag, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import moment from "moment";

import CustomPagination from "../../../../../core/common/customPagination/CustomPagination";
import { formatMillisecondsToSeconds } from "../../helpers/calculationsHelper";
import { EXECUTION_STATUS_COLORS } from "../../constants/automation.constants";

import type { ColumnsType } from "antd/es/table";
import type {
  ExecutionData,
  ExecutionPagination,
} from "../../types/automation.types";


interface ExecutionHistoryTableProps {
  executions: ExecutionData[];
  loading: boolean;
  pagination: ExecutionPagination;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onViewLogs: (record: ExecutionData) => void;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case "completed":
      return <CheckCircleOutlined />;
    case "failed":
      return <CloseCircleOutlined />;
    case "running":
      return <ClockCircleOutlined />;
    default:
      return null;
  }
};

const ExecutionHistoryTable: React.FC<ExecutionHistoryTableProps> = ({
  executions,
  loading,
  pagination,
  onPageChange,
  onLimitChange,
  onViewLogs,
}) => {
  const columns: ColumnsType<ExecutionData> = [
    {
      title: "Execution #",
      dataIndex: "execution_number",
      key: "execution_number",
      width: 130,
      sorter: (a, b) => a.execution_number - b.execution_number,
      render: (num) => <span className="fw-semibold">#{num}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <Tag color={EXECUTION_STATUS_COLORS[status] || "default"}>
          {getStatusIcon(status)} {status}
        </Tag>
      ),
    },
    {
      title: "Trigger",
      dataIndex: "trigger_type",
      key: "trigger_type",
      width: 180,
      render: (text, record) => (
        <div>
          <div className="fw-semibold" style={{ fontSize: 13 }}>
            {text}
          </div>
          <small className="text-muted" style={{ fontSize: 12 }}>
            {record.trigger_source}
          </small>
        </div>
      ),
    },
    {
      title: "Started At",
      dataIndex: "started_at",
      key: "started_at",
      width: 180,
      sorter: (a, b) =>
        new Date(a.started_at).getTime() - new Date(b.started_at).getTime(),
      render: (date) => (
        <Tooltip title={moment(date).format("YYYY-MM-DD HH:mm:ss")}>
          {moment(date).format("MMM DD, YYYY HH:mm")}
        </Tooltip>
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration_miliseconds",
      key: "duration_miliseconds",
      width: 120,
      sorter: (a, b) => (a.duration_seconds || 0) - (b.duration_seconds || 0),
      render: (miliseconds) => {
        if (!miliseconds) return <span className="text-muted">-</span>;
        return <span>{formatMillisecondsToSeconds(miliseconds)}</span>;
      },
    },
    {
      title: "Steps",
      key: "steps",
      width: 100,
      align: "center",
      render: (_, record) => {
        const totalSteps = record.steps?.length || 0;
        const completedSteps =
          record.steps?.filter((s) => s.status === "completed").length || 0;
        return (
          <span className="badge bg-info">
            {completedSteps}/{totalSteps}
          </span>
        );
      },
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
      width: 220,
      ellipsis: true,
      render: (error) =>
        error ? (
          <Tooltip title={error}>
            <span className="text-danger">{error.substring(0, 50)}...</span>
          </Tooltip>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="View Logs">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onViewLogs(record)}
          >
            View Logs
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="card-body p-0 mb-3 border-0">
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12, border: "1px solid #e2e8f0" }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            />
            <p className="mt-3 text-muted">Loading execution history...</p>
          </div>
        </div>
      ) : executions.length > 0 ? (
        <div
          className="bg-white"
          style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}
        >
          <Table
            className="custom-data-table"
            columns={columns}
            dataSource={executions}
            rowKey="id"
            size="middle"
            pagination={false}
            scroll={{ x: 1200 }}
            rowClassName={(_, index) =>
              index % 2 === 0
                ? "data-table-row-light"
                : "data-table-row-dark"
            }
          />
          <CustomPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages || 1}
            total={pagination.totalRecords}
            limit={pagination.limit}
            hasNextPage={pagination.hasNextPage}
            hasPrevPage={pagination.hasPrevPage}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12, border: "1px solid #e2e8f0" }}
        >
          <div className="text-center">
            <i className="ti ti-history-off" style={{ fontSize: 48, color: "#cbd5e1" }} />
            <h5 className="mt-3 mb-2 fw-semibold">No execution history found</h5>
            <p className="text-muted mb-0">Try adjusting your filters.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutionHistoryTable;
